package org.teddapp.project.ui.notes

import com.tedd.db.Note
import org.teddapp.project.portfolio.export.DocBlock
import org.teddapp.project.portfolio.export.PortfolioDoc
import org.teddapp.project.ui.skills.parseTags

/**
 * Sélecteur de fichier texte, fourni par la plateforme qui en a un : rend le nom et le
 * contenu du fichier choisi, ou null si l'on a annulé.
 */
object NoteImportPicker {
    var pick: (suspend () -> Pair<String, String>?)? = null
}

/**
 * Une note hors de l'appli : en Markdown, en PDF, et retour. Les dessins et les images
 * ne voyagent pas dans le texte ; ils y sont signalés à leur place.
 */
internal object NoteExport {

    /** Le texte de la note, ses dessins et ses images remplacés par une mention. */
    fun portableBody(body: String): String =
        body.split('\n').joinToString("\n") { line ->
            when {
                MarkdownEdits.inkIdOf(line) != null -> "*(dessin)*"
                MarkdownEdits.fileIdOf(line) != null -> "*(image)*"
                else -> line
            }
        }

    private fun title(note: Note) = note.title.orEmpty().ifBlank { "Sans titre" }

    /** La note en Markdown : son titre, ses étiquettes, son texte. */
    fun markdown(note: Note): String = buildString {
        append("# ").append(title(note)).append("\n\n")
        val tags = parseTags(note.tags)
        if (tags.isNotEmpty()) append(tags.joinToString(" ") { "#$it" }).append("\n\n")
        append(portableBody(note.body).trimEnd()).append('\n')
    }

    /** Un nom de fichier sûr d'après le titre : sans les signes que les systèmes refusent. */
    fun fileName(note: Note, extension: String): String {
        val base = title(note).replace(Regex("[\\\\/:*?\"<>|\\n\\r\\t]"), " ").replace(Regex("\\s+"), " ").trim().take(80)
        return base.ifBlank { "note" } + extension
    }

    /** La note en document, pour le PDF. */
    fun document(note: Note, bookName: String?, date: String): PortfolioDoc = PortfolioDoc(
        title = title(note),
        subtitle = listOfNotNull(bookName, parseTags(note.tags).takeIf { it.isNotEmpty() }?.joinToString(" ") { "#$it" })
            .joinToString(" · ").ifBlank { null },
        generatedAt = date,
        blocks = listOf(DocBlock.Markdown(portableBody(note.body))),
    )

    /**
     * Un fichier Markdown devenu note : un premier titre `# …` donne son titre, sinon le
     * nom du fichier. Le reste est le texte.
     */
    fun fromMarkdown(fileName: String, text: String): Pair<String, String> {
        val lines = text.replace("\r\n", "\n").split('\n')
        val first = lines.indexOfFirst { it.isNotBlank() }
        if (first >= 0 && lines[first].startsWith("# ")) {
            val title = lines[first].removePrefix("# ").trim()
            val body = lines.drop(first + 1).joinToString("\n").trim('\n')
            return title to body
        }
        val title = fileName.substringAfterLast('/').substringAfterLast('\\').substringBeforeLast('.').trim()
        return title.ifBlank { "Note importée" } to text.replace("\r\n", "\n").trim('\n')
    }
}
